import { useEffect, useState } from 'react';

/**
 * The height of what the person can actually see — on a phone that is the
 * window minus the keyboard. A fixed sheet sized to `dvh` keeps its full
 * height when the keyboard opens and its foot ends up under the keys; sized
 * to this, it shrinks and scrolls instead. Null when the browser has no
 * visualViewport (then the CSS max-height stays in charge).
 */
export function useVisualViewportHeight(active = true): number | null {
  const [height, setHeight] = useState<number | null>(null);
  useEffect(() => {
    if (!active) return;
    const vv = typeof window !== 'undefined' ? window.visualViewport : null;
    if (!vv) return;
    const read = () => setHeight(Math.round(vv.height));
    read();
    vv.addEventListener('resize', read);
    vv.addEventListener('scroll', read);
    return () => {
      vv.removeEventListener('resize', read);
      vv.removeEventListener('scroll', read);
    };
  }, [active]);
  // The page behind the sheet must not take the scroll (iOS scrolls the body
  // when the sheet cannot) — lock it while the sheet is open.
  useEffect(() => {
    if (!active || typeof document === 'undefined') return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [active]);
  return height;
}
